import { useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ExternalLink, MessageCircle, Radio, Send, Video } from "lucide-react";
import { IconAd } from "@tabler/icons-react";

import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { SpinnerBlock } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import {
  getTikTokSample,
  type TikTokRejectReason,
  type TikTokSampleApplication,
  type TikTokSampleContent,
} from "@/services/tiktokShop.service";
import { formatCompactCount } from "@/utils/formatters";

import {
  AD_REQUEST_STATUS_LABEL,
  CANCELLED_LABEL,
  errorMessage,
  orPrivate,
  REJECT_REASON_LABEL,
  SAMPLE_STAGE,
  shortDate,
  timeLeft,
  usd,
} from "../tiktokFormat";
import { SuggestionChip } from "./SuggestionChip";
import { RequestForAdsDialog } from "./RequestForAdsDialog";

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-right text-sm tabular-nums text-foreground">{children}</dd>
    </div>
  );
}

const STEPS = ["Requested", "Approved", "Shipped", "Delivered", "Posted"];

function Timeline({ status }: { status: string }) {
  const stage = status === "PENDING" ? 0 : SAMPLE_STAGE[status]?.step;
  if (stage === undefined) {
    return <p className="text-sm text-muted-foreground">{CANCELLED_LABEL[status] ?? status}</p>;
  }
  return (
    <ol className="grid grid-cols-5 gap-1">
      {STEPS.map((label, i) => (
        <li key={label} className="flex flex-col gap-1.5">
          <span className={cn("h-1.5 rounded-full", i <= stage ? "bg-brand-500" : "bg-muted")} />
          <span className={cn("text-[11px]", i <= stage ? "text-foreground" : "text-muted-foreground")}>{label}</span>
        </li>
      ))}
    </ol>
  );
}

function ContentCard({ content, onRequest }: { content: TikTokSampleContent; onRequest: (c: TikTokSampleContent) => void }) {
  const link = content.pageLink ?? content.url;
  const request = content.adRequest;
  const requestOpen = request && !["REJECTED", "CANCELLED", "EXPIRED"].includes(request.status);
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-start justify-between gap-3">
        <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          {content.format === "LIVE" ? <Radio className="h-3.5 w-3.5" /> : <Video className="h-3.5 w-3.5" />}
          {content.format === "LIVE" ? "LIVE" : "Video"} · {shortDate(content.postedAt)}
        </span>
        {link && (
          <a href={link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-brand-700 hover:underline dark:text-brand-400">
            Open <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      {content.description && <p className="mt-2 line-clamp-2 text-sm text-foreground">{content.description}</p>}
      <dl className="mt-3 grid grid-cols-4 gap-2 text-center">
        {[
          ["Views", content.viewCount],
          ["Likes", content.likeCount],
          ["Comments", content.commentCount],
          ["Paid orders", content.paidOrderCount],
        ].map(([label, value]) => (
          <div key={label as string}>
            <dd className="text-sm font-semibold tabular-nums text-foreground">{formatCompactCount(value as number)}</dd>
            <dt className="text-[11px] text-muted-foreground">{label}</dt>
          </div>
        ))}
      </dl>
      {content.format === "VIDEO" && (
        <div className="mt-3 flex items-center justify-between gap-3 border-t border-border pt-3">
          {requestOpen ? (
            <span className="text-xs text-muted-foreground">
              Ad request: <span className="text-foreground">{AD_REQUEST_STATUS_LABEL[request.status]}</span>
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">Run it as a Meta ad</span>
          )}
          {!requestOpen && (
            <Button type="button" size="sm" variant="outline" onClick={() => onRequest(content)}>
              <IconAd size={14} stroke={1.75} aria-hidden />
              Request for ads
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** One sample request: who asked, for what, where it stands, and what it produced. */
export function SampleDetailSheet({
  id,
  onOpenChange,
  onReview,
  onMessage,
  onInvite,
}: {
  id: string | null;
  onOpenChange: (open: boolean) => void;
  onReview: (s: TikTokSampleApplication, result: "APPROVE" | "REJECT", reason?: TikTokRejectReason) => Promise<void>;
  onMessage: (s: TikTokSampleApplication) => void;
  onInvite: (s: TikTokSampleApplication) => void;
}) {
  const queryClient = useQueryClient();
  const [requestVideo, setRequestVideo] = useState<TikTokSampleContent | null>(null);
  const detail = useQuery({
    queryKey: ["tiktok-sample", id],
    queryFn: () => getTikTokSample(id!),
    enabled: id !== null,
  });
  const s = detail.data;
  const decision = s?.status === "PENDING" ? s.suggestion : s?.autoDecision;
  const left = s ? timeLeft(s.approveExpiresAt) : null;
  const totals = s?.contents.reduce(
    (acc, c) => ({ views: acc.views + c.viewCount, orders: acc.orders + c.paidOrderCount }),
    { views: 0, orders: 0 },
  );

  return (
    <Sheet open={id !== null} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        {!detail.data && <SheetTitle className="sr-only">Sample request</SheetTitle>}
        {detail.isLoading && <SpinnerBlock className="py-24" />}
        {detail.error && <p className="py-24 text-center text-sm text-muted-foreground">{errorMessage(detail.error)}</p>}
        {s && (
          <div className="space-y-6">
            <SheetHeader className="space-y-3 text-left">
              <div className="flex items-center gap-3">
                <UserAvatar src={s.creator.avatarUrl} name={s.creator.nickname || s.creator.username} className="h-14 w-14 shrink-0 text-base" />
                <div className="min-w-0">
                  <SheetTitle className="truncate">{s.creator.nickname || `@${s.creator.username}`}</SheetTitle>
                  <SheetDescription className="truncate">
                    @{s.creator.username}
                    {s.affiliate ? ` · your affiliate ${s.affiliate.name}` : ""}
                  </SheetDescription>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => onMessage(s)}>
                  <MessageCircle className="h-3.5 w-3.5" />
                  Message
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => onInvite(s)}>
                  <Send className="h-3.5 w-3.5" />
                  Invite to collab
                </Button>
                <Button type="button" size="sm" variant="ghost" asChild>
                  <a href={`https://www.tiktok.com/@${s.creator.username}`} target="_blank" rel="noreferrer">
                    <ExternalLink className="h-3.5 w-3.5" />
                    TikTok
                  </a>
                </Button>
              </div>
            </SheetHeader>

            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">Where it stands</h3>
              <Timeline status={s.status} />
              {s.status === "PENDING" && (
                <div className="space-y-3 rounded-lg bg-muted/50 p-3">
                  {decision && <SuggestionChip suggestion={decision} />}
                  {decision && decision.reasons.length > 1 && (
                    <ul className="list-disc space-y-0.5 pl-4 text-xs text-muted-foreground">
                      {decision.reasons.slice(1).map((r) => (
                        <li key={r}>{r}</li>
                      ))}
                    </ul>
                  )}
                  {s.partnerName ? (
                    <p className="text-sm text-muted-foreground">{s.partnerName} applied for the creator, so the agency reviews this request on TikTok.</p>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button type="button" size="sm" disabled={!s.isApprovable} onClick={() => onReview(s, "APPROVE")}>
                        <Check className="h-3.5 w-3.5" />
                        Approve sample
                      </Button>
                      {(Object.keys(REJECT_REASON_LABEL) as TikTokRejectReason[]).slice(0, 2).map((reason) => (
                        <Button key={reason} type="button" size="sm" variant="outline" onClick={() => onReview(s, "REJECT", reason)}>
                          Reject: {REJECT_REASON_LABEL[reason].toLowerCase()}
                        </Button>
                      ))}
                    </div>
                  )}
                  {!s.isApprovable && s.disapprovableReasons.length > 0 && (
                    <p className="text-xs text-red-600 dark:text-red-400">TikTok will not allow approval: {s.disapprovableReasons.join(", ")}</p>
                  )}
                  {left && <p className="text-xs text-muted-foreground">TikTok cancels it if nobody reviews it within {left.label}.</p>}
                </div>
              )}
              {s.reviewedBy && (
                <p className="text-xs text-muted-foreground">
                  {s.reviewedBy === "AUTO" ? "Reveshare’s review rules" : "You"} {s.rejectReason ? "rejected" : "approved"} it {shortDate(s.reviewedAt)}
                  {s.rejectReason ? ` (${REJECT_REASON_LABEL[s.rejectReason].toLowerCase()})` : ""}.
                  {s.reviewedBy === "AUTO" && decision ? ` ${decision.reasons.join("; ")}.` : ""}
                </p>
              )}
            </section>

            <section>
              <h3 className="mb-1 text-sm font-semibold text-foreground">Product</h3>
              <div className="flex items-center gap-3 py-1">
                {s.product.imageUrl && <img src={s.product.imageUrl} alt="" className="h-12 w-12 rounded-md object-cover" />}
                <div className="min-w-0">
                  <p className="truncate text-sm text-foreground">{s.product.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {s.product.skuName ?? "Default"}
                    {s.commissionRate ? ` · ${Math.round(Number(s.commissionRate) * 100)}% commission` : ""}
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="mb-1 text-sm font-semibold text-foreground">Creator, last 30 days</h3>
              <dl className="divide-y divide-border">
                <Row label="Followers">{orPrivate(s.creator.followerCount, formatCompactCount)}</Row>
                <Row label="GMV from shoppable content">{orPrivate(s.creator.gmv, usd)}</Row>
                <Row label="Sample fulfillment, 90 days">{orPrivate(s.creator.fulfillmentRate, (n) => `${Math.round(n)}%`)}</Row>
                <Row label="Median video views">{orPrivate(s.creator.medianViews, formatCompactCount)}</Row>
                <Row label="Shoppable posts">{orPrivate(s.creator.contentCount, String)}</Row>
              </dl>
            </section>

            {(s.contents.length > 0 || ["CONTENT_PENDING", "COMPLETED", "OPS_COMPLETED"].includes(s.status)) && (
              <section className="space-y-2">
                <div className="flex items-baseline justify-between">
                  <h3 className="text-sm font-semibold text-foreground">What it produced</h3>
                  {totals && s.contents.length > 0 && (
                    <span className="text-xs tabular-nums text-muted-foreground">
                      {formatCompactCount(totals.views)} views · {totals.orders} paid orders
                    </span>
                  )}
                </div>
                {s.contents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nothing posted yet. TikTok expects a video or LIVE once the sample arrives.</p>
                ) : (
                  s.contents.map((c) => <ContentCard key={c.contentId} content={c} onRequest={setRequestVideo} />)
                )}
              </section>
            )}
          </div>
        )}
      </SheetContent>
      <RequestForAdsDialog
        open={requestVideo !== null}
        onClose={() => setRequestVideo(null)}
        video={requestVideo}
        creatorUsername={s?.creator.username ?? ""}
        onCreated={() => void queryClient.invalidateQueries({ queryKey: ["tiktok-sample", id] })}
      />
    </Sheet>
  );
}
